import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import classNames from 'classnames/bind';
import List from 'yoda-core-components/lib/components/List/List';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import Button from 'yoda-core-components/lib/components/Button/Button';
import * as styles from './ErrorPage.css';
import { data, nativeAppdata } from './ErrorTemplateData';
import * as errorPageActions from '../../actions/ErrorPageAction';
import ErrorPageReducer from '../../reducers/ErrorPageReducer';

const cx = classNames.bind(styles);

/**
 * ErrorPages component to standardize the styling and html structure of any ErrorPage component on the page
 * This supports "ErroPage type of 404 and 500" based on the prop value of template Type
 * */

class Error extends Component {
    static propTypes = {
        templateType: PropTypes.string.isRequired,
        actions: PropTypes.object.isRequired,
        deviceType: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    };

    static defaultProps = {
        templateType: '500',
        actions: {},
        deviceType: {},
    };

    static helpfulListRenderer(item) {
        const { iconComponent } = item;
        return (
            <div className={cx('listItem')}>
                <Icon
                    className={cx('icon')}
                    iconType="svg"
                    width="40px"
                    height="40px"
                    viewBox="0 0 40 40"
                >
                    {iconComponent}
                </Icon>
                {item.name === 'Back to Previous Page' ? (
                    <Button
                        className={cx('links')}
                        buttonType="Text"
                        size="Md"
                        onClick={() => {
                            window.history.back();
                        }}
                    >
                        {item.name}
                    </Button>
                ) : (
                    <a className={cx('links')} href={item.link}>
                        {item.name}
                    </a>
                )}
            </div>
        );
    }

    static render500Template(templateType = '500') {
        return (
            <div>
                <p className={cx('listHeader')}>{data[templateType].detailsData}</p>
                <Button
                    type="button"
                    automationId="at-error-link"
                    rel="noopener noreferrer"
                    onClick={() => {
                        window.location.reload();
                    }}
                    className={cx('refreshButton')}
                >
                    Refresh Page
                </Button>
            </div>
        );
    }

    constructor() {
        super();
        this.renderTemplate = this.renderTemplate.bind(this);
        this.assistanceListRenderer = this.assistanceListRenderer.bind(this);
    }

    componentDidMount() {
        this.props.actions.ErrorPageLoaded(this.props.templateType);
    }

    assistanceListRenderer(item) {
        const devicetypeCheck = this.props.deviceType.isTablet || this.props.deviceType.isMobile;
        /* istanbul ignore next */
        const telNum = devicetypeCheck ? `tel:${item.details}` : '';
        const { iconComponent } = item;
        /* istanbul ignore next */
        const telClassName = devicetypeCheck
            ? cx('details-information')
            : cx('inactive', 'details-information');
        return (
            <div className={cx('listItem')}>
                <Icon
                    className={cx('svg-icon')}
                    iconType="svg"
                    width="25px"
                    height="25px"
                    viewBox="0 0 40 40"
                    pathClassName={cx('infoIcon')}
                >
                    {iconComponent}
                </Icon>
                <div className={cx('detailsWrapper')}>
                    <div className={cx('details')}>{item.name}</div>
                    {item.name === 'Give us a call' ? (
                        <a href={telNum} className={telClassName}>
                            {item.details}
                        </a>
                    ) : (
                        <a
                            href={item.link}
                            target={item.newTab}
                            className={cx('details-information')}
                        >
                            {item.details}
                        </a>
                    )}
                </div>
            </div>
        );
    }

    loadLinksData(templateType) {
        // Removed switch cases as we are returning the same content
        return data && data[templateType] && data[templateType].leftData;
    }

    loadAssistanceData(templateType) {
        const { isNative } = this.props;
        // Removed switch cases as we are returning the same content
        const assistanceData = isNative
            ? nativeAppdata && nativeAppdata.rightData
            : data && data[templateType] && data[templateType].rightData;
        return assistanceData;
    }

    renderTemplate(templateType) {
        const { isNative } = this.props;
        switch (templateType) {
            case '404':
                return (
                    <div>
                        <p className={cx('listHeader', 'listHeaderOne', isNative && 'mx-1')}>
                            {data[templateType].detailsData}
                        </p>
                        {!isNative && (
                            <p className={cx('listHeader')}>{data[templateType].informationData}</p>
                        )}
                    </div>
                );

            case '500':
            default:
                return Error.render500Template(templateType);
        }
    }

    render() {
        const { isNative } = this.props;
        /* istanbul ignore next */
        const templateType = [404, 500].includes(+this.props.templateType)
            ? this.props.templateType
            : '500';

        // ClassNames added dynamically based on template type
        const classNameForLinks =
            templateType === '500'
                ? cx('listContainer', 'helpfulLinks', 'links-wrapper-500')
                : cx('listContainer', 'helpfulLinks', 'links-wrapper');

        return (
            <div className={cx('parentWrapper')}>
                <h3 className={cx('errorTitle')}>{data[templateType].errorInformation}</h3>
                {this.renderTemplate(templateType)}
                <div className={cx('linkContainer')}>
                    {!isNative && (
                        <div className={classNameForLinks}>
                            <h3 className={cx('linksTitle')}>{data[templateType].linkstitle}</h3>
                            <List
                                datasource={this.loadLinksData(templateType)}
                                spacing="None"
                                direction="Vertical"
                                itemSpacing="None"
                                listBodyClass={cx('list-class')}
                                listStyleClass={cx('list-ul-class')}
                                itemStyleClass={cx('list-item-class')}
                                childRenderer={Error.helpfulListRenderer}
                                automationId="at-error-helpfullinks-renderer"
                            />
                        </div>
                    )}
                    <div className={cx('listContainer', 'assistance-wrapper', isNative && 'mt-15')}>
                        <h3 className={cx('linksTitle', 'linksTitle-assistance')}>
                            {data[templateType].assistancetitle}
                        </h3>
                        <List
                            datasource={this.loadAssistanceData(templateType)}
                            spacing="None"
                            direction="Vertical"
                            itemSpacing="None"
                            listBodyClass={cx('list-class')}
                            listStyleClass={cx('list-ul-class')}
                            itemStyleClass={cx('list-item-class')}
                            childRenderer={this.assistanceListRenderer}
                            automationId="at-error-assistancelinks-renderer"
                        />
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (store) => ({
    deviceType: store.context && store.context.deviceType ? store.context.deviceType : {},
    isNative: get(store, 'context.isNative', false),
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(errorPageActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Error);
export { errorPageActions, ErrorPageReducer };
